import { Fragment, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import {
  api, ApiError,
  type ProductRow, type ScorecardAnalysis, type ScorecardCatalogEntry,
  type ScorecardHighlight, type ScorecardMetric, type ScorecardView,
} from "../api";
import { useAsync, bustAsyncCache } from "../useAsync";
import { DASH, money, count } from "../format";
import { go } from "../router";

const WEEK_CHOICES = [4, 12, 26];
const TEMPLATES = [
  { id: "default" as const, label: "From sources" },
  { id: "saas_waitlist" as const, label: "SaaS waitlist" },
  { id: "paid_acquisition" as const, label: "Paid acquisition" },
  { id: "content_led" as const, label: "Content-led" },
];

function fmt(value: number | null, format: ScorecardView["sections"][number]["metrics"][number]["format"], currency: string): string {
  if (value === null) return DASH;
  if (format === "cents") return money(value, currency);
  if (format === "percent") return `${(value * 100).toFixed(1)}%`;
  return count(value);
}

function wowLabel(wow: number | null): { text: string; color: string } {
  if (wow === null) return { text: DASH, color: "var(--text-faint)" };
  const sign = wow > 0 ? "+" : "−";
  const text = `${sign}${Math.abs(wow * 100).toFixed(1)}%`;
  if (Math.abs(wow) < 0.005) return { text: "flat", color: "var(--text-4)" };
  return { text, color: wow > 0 ? "var(--pos)" : "var(--neg)" };
}

export function Scorecard({ product }: { product: ProductRow }) {
  const [weeks, setWeeks] = useState(12);
  const [showCurrent, setShowCurrent] = useState(false);
  const [editor, setEditor] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const view = useAsync(() => api.scorecard.get(product.slug, weeks, showCurrent), [product.slug, weeks, showCurrent]);
  const analysis = useAsync(() => api.scorecard.insights(product.slug, weeks), [product.slug, weeks]);

  if (view.error) return <PageProblem message={view.error} />;
  if (view.loading || !view.data) return <PageLoading />;
  const v = view.data;

  async function refresh() {
    bustAsyncCache();
    await Promise.all([view.reload(), analysis.reload()]);
  }

  async function run(label: string, fn: () => Promise<void>) {
    setBusy(label);
    setErr(null);
    try {
      await fn();
      await refresh();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="page" style={{ paddingTop: 26 }}>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <h1>Scorecard</h1>
          <span className="sub">
            Weekly growth KPIs for {product.name} — warehouse cells fill themselves; type the rest.
            {v.clamped ? " History on this plan is shorter than the range you asked for." : ""}
          </span>
        </div>
        <span style={{ flex: 1 }} />
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          {WEEK_CHOICES.map((w) => (
            <button key={w} type="button" className="seg" aria-pressed={weeks === w} onClick={() => setWeeks(w)}>
              {w}w
            </button>
          ))}
          <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "var(--text-3)", marginLeft: 6, cursor: "pointer" }}>
            <input type="checkbox" checked={showCurrent} onChange={(e) => setShowCurrent(e.target.checked)} />
            This week so far
          </label>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", padding: "16px 0 8px" }}>
        <button type="button" className="btn" onClick={() => setEditor(true)}>Customise</button>
        <button type="button" className="btn" disabled={!!busy} onClick={() => run("csv", () => api.scorecard.download(product.slug, "csv", weeks, showCurrent))}>
          {busy === "csv" ? "Exporting…" : "Export CSV"}
        </button>
        <button type="button" className="btn" disabled={!!busy} onClick={() => run("xlsx", () => api.scorecard.download(product.slug, "xlsx", weeks, showCurrent))}>
          {busy === "xlsx" ? "Exporting…" : "Export Excel"}
        </button>
        <button type="button" className="btn" disabled={!!busy} onClick={() => run("share", async () => {
          const r = await api.scorecard.share(product.slug);
          await navigator.clipboard.writeText(r.url);
        })}>
          {busy === "share" ? "Minting…" : v.share_url ? "Copy share link" : "Share"}
        </button>
        {v.share_url && (
          <button type="button" className="btn" disabled={!!busy} onClick={() => run("revoke", () => api.scorecard.revokeShare(product.slug).then(() => {}))}>
            Revoke share
          </button>
        )}
        {v.sheets_enabled && (
          <button type="button" className="btn" disabled={!!busy} onClick={() => run("sheets", async () => {
            const r = await api.scorecard.sheetsSync(product.slug, weeks);
            window.open(r.url, "_blank", "noopener");
          })}>
            {busy === "sheets" ? "Syncing…" : v.spreadsheet_id ? "Sync Sheet" : "Push to Sheets"}
          </button>
        )}
      </div>
      {err && <div className="form-error" style={{ marginBottom: 12 }}>{err}</div>}

      <div style={{ display: "flex", gap: 18, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 620px", minWidth: 0 }}>
          <ScorecardGrid view={v} slug={product.slug} onSaved={refresh} highlights={analysis.data?.highlights} />
        </div>
        <InsightRail analysis={analysis.data ?? null} loading={analysis.loading} />
      </div>

      {editor && (
        <EditorDrawer
          product={product}
          view={v}
          onClose={() => setEditor(false)}
          onSaved={async () => { setEditor(false); await refresh(); }}
        />
      )}
    </div>
  );
}

const HIGHLIGHT_BG: Record<ScorecardHighlight["kind"], string> = {
  good: "var(--pos-bg)",
  bad: "var(--neg-bg)",
  watch: "var(--warn-bg)",
};
const HIGHLIGHT_FG: Record<ScorecardHighlight["kind"], string> = {
  good: "var(--pos)",
  bad: "var(--neg)",
  watch: "var(--warn)",
};

function ScorecardGrid({ view, slug, onSaved, readOnly = false, highlights }: {
  view: ScorecardView; slug: string; onSaved?: () => Promise<void>; readOnly?: boolean;
  highlights?: ScorecardHighlight[];
}) {
  const hl = useMemo(() => {
    const map = new Map<string, ScorecardHighlight>();
    for (const h of highlights ?? []) map.set(`${h.metric_key}|${h.week_start}`, h);
    return map;
  }, [highlights]);
  const scroller = useRef<HTMLDivElement>(null);
  // Newest weeks live on the right; land there instead of on ancient history.
  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [view.weeks.length, view.weeks[view.weeks.length - 1]?.week_start]);
  return (
    <div ref={scroller} style={{ overflowX: "auto", marginTop: 8, border: "1px solid var(--border)", borderRadius: 10 }}>
      <table className="scorecard-grid" style={{ borderCollapse: "collapse", minWidth: "100%", fontVariantNumeric: "tabular-nums" }}>
        <thead>
          <tr>
            <th style={thSticky}>Metric</th>
            {view.weeks.map((w) => (
              <th key={w.week_start} style={th} title={`Week ending ${w.week_end}`}>
                {w.label}
              </th>
            ))}
            <th style={th}>WoW</th>
          </tr>
        </thead>
        <tbody>
          {view.sections.map((s) => (
            <Fragment key={s.id}>
              <tr>
                <td colSpan={view.weeks.length + 2} style={sectionHead}>{s.label}</td>
              </tr>
              {s.metrics.map((m) => {
                const wow = wowLabel(m.wow);
                return (
                  <tr key={m.key}>
                    <td style={tdSticky} title={m.source ?? undefined}>
                      {m.label}
                      {m.kind === "manual" && <span style={{ color: "var(--text-faint)", marginLeft: 6, fontSize: 11 }}>manual</span>}
                    </td>
                    {m.values.map((c) => {
                      const h = hl.get(`${m.key}|${c.week_start}`);
                      const cellStyle = h
                        ? { ...td, background: HIGHLIGHT_BG[h.kind], color: HIGHLIGHT_FG[h.kind], fontWeight: 600 }
                        : td;
                      return (
                        <td key={c.week_start} style={cellStyle} title={h?.note}>
                          {m.kind === "manual" && !readOnly && onSaved
                            ? <ManualCell slug={slug} metricKey={m.key} weekStart={c.week_start} value={c.value} format={m.format} currency={view.currency} onSaved={onSaved} />
                            : <span className="num">{fmt(c.value, m.format, view.currency)}</span>}
                        </td>
                      );
                    })}
                    <td style={{ ...td, color: wow.color }} className="num">{wow.text}</td>
                  </tr>
                );
              })}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const RAIL_ACCENT: Record<ScorecardRailCardType, { color: string; label: string }> = {
  insight: { color: "var(--keeper)", label: "Insight" },
  query: { color: "var(--warn)", label: "Worth asking" },
  action: { color: "var(--pos)", label: "Action" },
};
type ScorecardRailCardType = ScorecardAnalysis["cards"][number]["type"];

function InsightRail({ analysis, loading }: { analysis: ScorecardAnalysis | null; loading: boolean }) {
  return (
    <aside style={{ flex: "0 1 300px", minWidth: 260, display: "flex", flexDirection: "column", gap: 10 }}>
      <div className="eyebrow" style={{ marginTop: 8 }}>
        This week{analysis?.ai ? " · Keeper" : ""}
      </div>
      {loading && !analysis && <span className="sub">Reading the grid…</span>}
      {!loading && analysis && analysis.cards.length === 0 && (
        <span className="sub">Nothing moved enough this week to call out.</span>
      )}
      {analysis?.cards.map((c, i) => {
        const accent = RAIL_ACCENT[c.type];
        return (
          <div key={`${c.type}-${i}`} className="panel" style={{
            padding: "12px 14px", borderLeft: `3px solid ${accent.color}`, borderRadius: 8,
          }}>
            <div style={{ fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase", color: accent.color, fontWeight: 600 }}>
              {accent.label}
            </div>
            <div style={{ fontSize: 13.5, fontWeight: 600, marginTop: 4 }}>{c.headline}</div>
            <div className="num" style={{ fontSize: 12, color: "var(--text-2)", marginTop: 3 }}>{c.metric_line}</div>
            {c.detail && <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 5 }}>{c.detail}</div>}
          </div>
        );
      })}
    </aside>
  );
}

function ManualCell({ slug, metricKey, weekStart, value, format, currency, onSaved }: {
  slug: string; metricKey: string; weekStart: string; value: number | null;
  format: ScorecardView["sections"][number]["metrics"][number]["format"];
  currency: string; onSaved: () => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value == null ? "" : format === "cents" ? String(value / 100) : String(value));
  if (!editing) {
    return (
      <button type="button" className="num" style={{ background: "none", border: 0, cursor: "text", color: "inherit", width: "100%", textAlign: "right" }}
        onClick={() => setEditing(true)}>
        {fmt(value, format, currency)}
      </button>
    );
  }
  return (
    <input
      autoFocus
      className="num"
      style={{ width: 72, textAlign: "right", fontSize: 12 }}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={async () => {
        const raw = draft.trim();
        const next = raw === "" ? null : format === "cents" ? Math.round(Number(raw) * 100) : Number(raw);
        setEditing(false);
        if (next !== value && (next === null || Number.isFinite(next))) {
          await api.scorecard.values(slug, [{ metric_key: metricKey, week_start: weekStart, value: next }]);
          await onSaved();
        }
      }}
      onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); if (e.key === "Escape") setEditing(false); }}
    />
  );
}

function EditorDrawer({ product, view, onClose, onSaved }: {
  product: ProductRow; view: ScorecardView; onClose: () => void; onSaved: () => Promise<void>;
}) {
  const [sections, setSections] = useState(() => view.sections.map((s) => ({
    id: s.id, label: s.label,
    metrics: s.metrics.map(({ source: _s, values: _v, wow: _w, ...m }) => m),
  })));
  const [suggesting, setSuggesting] = useState(false);
  const [notes, setNotes] = useState<Array<{ key: string; reason: string }>>([]);
  const [err, setErr] = useState<string | null>(null);
  const [manualLabel, setManualLabel] = useState("");
  const catalog = view.catalog;
  const present = useMemo(() => new Set(sections.flatMap((s) => s.metrics.map((m) => m.key))), [sections]);
  const grouped = useMemo(() => {
    const map = new Map<string, ScorecardCatalogEntry[]>();
    for (const e of catalog) {
      const list = map.get(e.sectionLabel) ?? [];
      list.push(e);
      map.set(e.sectionLabel, list);
    }
    return [...map.entries()];
  }, [catalog]);

  function toggle(entry: ScorecardCatalogEntry) {
    setSections((prev) => {
      const has = prev.some((s) => s.metrics.some((m) => m.key === entry.key));
      if (has) {
        return prev
          .map((s) => ({ ...s, metrics: s.metrics.filter((m) => m.key !== entry.key) }))
          .filter((s) => s.metrics.length > 0);
      }
      const metric: ScorecardMetric = {
        key: entry.key, label: entry.label, kind: entry.kind,
        metric_ref: entry.kind === "auto" ? entry.key : undefined,
        formula: entry.formula, format: entry.format,
      };
      const idx = prev.findIndex((s) => s.id === entry.sectionId);
      if (idx >= 0) {
        const copy = prev.map((s) => ({ ...s, metrics: [...s.metrics] }));
        copy[idx]!.metrics.push(metric);
        return copy;
      }
      return [...prev, { id: entry.sectionId, label: entry.sectionLabel, metrics: [metric] }];
    });
  }

  async function save() {
    setErr(null);
    try {
      await api.scorecard.save(product.slug, { sections, template: view.template });
      await onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(25,26,36,0.35)", zIndex: 40,
      display: "flex", justifyContent: "flex-end",
    }} onClick={onClose}>
      <aside className="panel" style={{
        width: "min(440px, 100%)", height: "100%", overflow: "auto",
        borderRadius: 0, padding: 22,
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>Customise scorecard</h2>
          <span style={{ flex: 1 }} />
          <button type="button" className="btn" onClick={onClose}>Close</button>
        </div>
        <p className="sub" style={{ marginTop: 8 }}>
          Tick catalog metrics, add a manual row, or apply a template. Suggestions never save themselves.
        </p>

        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "14px 0" }}>
          {TEMPLATES.map((t) => (
            <button key={t.id} type="button" className="seg" onClick={async () => {
              try {
                await api.scorecard.template(product.slug, t.id);
                await onSaved();
              } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
            }}>{t.label}</button>
          ))}
          <button type="button" className="seg" disabled={suggesting} onClick={async () => {
            setSuggesting(true); setErr(null);
            try {
              const r = await api.scorecard.suggest(product.slug);
              setNotes(r.notes);
              const byKey = new Map(r.metrics.map((m) => [m.key, m]));
              const next = new Map<string, { id: string; label: string; metrics: ScorecardMetric[] }>();
              for (const m of r.metrics) {
                const cat = catalog.find((c) => c.key === m.key);
                const sid = cat?.sectionId ?? "custom";
                const slabel = cat?.sectionLabel ?? "Custom";
                const bucket = next.get(sid) ?? { id: sid, label: slabel, metrics: [] };
                bucket.metrics.push(byKey.get(m.key)!);
                next.set(sid, bucket);
              }
              setSections([...next.values()]);
            } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
            finally { setSuggesting(false); }
          }}>{suggesting ? "Suggesting…" : "Suggest metrics"}</button>
        </div>
        {notes.length > 0 && (
          <ul style={{ fontSize: 12, color: "var(--text-2)", paddingLeft: 18 }}>
            {notes.map((n) => <li key={n.key}>{n.reason}</li>)}
          </ul>
        )}

        {grouped.map(([label, entries]) => (
          <div key={label} style={{ marginTop: 16 }}>
            <div className="eyebrow">{label}</div>
            {entries.map((e) => (
              <label key={e.key} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "6px 0", fontSize: 13 }}>
                <input type="checkbox" checked={present.has(e.key)} onChange={() => toggle(e)} />
                <span>
                  <strong>{e.label}</strong>
                  <span style={{ color: "var(--text-faint)", marginLeft: 6 }}>{e.kind}</span>
                  <div style={{ color: "var(--text-3)", fontSize: 12 }}>{e.description}</div>
                </span>
              </label>
            ))}
          </div>
        ))}

        <div style={{ marginTop: 18 }}>
          <div className="eyebrow">Add a manual metric</div>
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <input value={manualLabel} onChange={(e) => setManualLabel(e.target.value)} placeholder="e.g. Newsletter sends" style={{ flex: 1 }} />
            <button type="button" className="btn" onClick={() => {
              const label = manualLabel.trim();
              if (!label) return;
              const key = `manual.${label.toLowerCase().replace(/[^a-z0-9]+/g, ".").replace(/^\.|\.$/g, "")}`;
              setSections((prev) => {
                const custom = prev.find((s) => s.id === "custom");
                const metric: ScorecardMetric = { key, label, kind: "manual", format: "int" };
                if (custom) return prev.map((s) => s.id === "custom" ? { ...s, metrics: [...s.metrics, metric] } : s);
                return [...prev, { id: "custom", label: "Custom", metrics: [metric] }];
              });
              setManualLabel("");
            }}>Add</button>
          </div>
        </div>

        {err && <div className="form-error" style={{ marginTop: 12 }}>{err}</div>}
        <div style={{ display: "flex", gap: 8, marginTop: 22 }}>
          <button type="button" className="btn-dark" onClick={save}>Save layout</button>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
        </div>
      </aside>
    </div>
  );
}

export function ScorecardPublic({ token }: { token: string }) {
  const view = useAsync(() => api.scorecard.public(token), [token]);
  if (view.error) return <PageProblem message={view.error} />;
  if (view.loading || !view.data) return <PageLoading />;
  return (
    <div className="page" style={{ padding: "32px 24px", maxWidth: 1100, margin: "0 auto" }}>
      <div className="eyebrow">Weekly scorecard</div>
      <h1 style={{ marginTop: 6 }}>{view.data.product_name}</h1>
      <span className="sub">Auto-filled by FunnelKeeper. Figures are read-only on this link.</span>
      <ScorecardGrid view={view.data} slug={view.data.product_slug} readOnly />
      <p className="sub" style={{ marginTop: 18 }}>
        <button type="button" className="btn" onClick={() => go({ name: "signup" })}>Start a scorecard of your own</button>
      </p>
    </div>
  );
}

const th: CSSProperties = { textAlign: "right", padding: "8px 10px", fontSize: 11, fontWeight: 500, color: "var(--text-3)", borderBottom: "1px solid var(--border)", whiteSpace: "nowrap" };
const thSticky: CSSProperties = { ...th, textAlign: "left", position: "sticky", left: 0, background: "var(--surface)", zIndex: 1 };
const td: CSSProperties = { textAlign: "right", padding: "7px 10px", fontSize: 12.5, borderBottom: "1px solid var(--border-hairline)" };
const tdSticky: CSSProperties = { ...td, textAlign: "left", position: "sticky", left: 0, background: "var(--surface)", whiteSpace: "nowrap" };
const sectionHead: CSSProperties = { padding: "8px 10px", fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", background: "var(--text)", color: "var(--surface)", fontWeight: 600 };

function PageLoading() {
  return <div className="page" style={{ paddingTop: 48 }}><span className="sub">Loading scorecard…</span></div>;
}
function PageProblem({ message }: { message: string }) {
  return <div className="page" style={{ paddingTop: 48 }}><div className="form-error">{message}</div></div>;
}
